import { Engine, HelperFunctions, State } from "../../tsthree";
import { ENGINE_DEBUG_MODE, GAME_DEBUG_MODE, PUPPETEER_MODE } from "../../engine/Constants/Constants";
import { PER_SLOT_KEYS, PlayerDataSingleton } from "../Logic/PlayerDataSingleton";
import { SAVE_KEYS } from "../Constants/SaveKeys";
import { EntrypointTypes } from "../Constants/EntrypointTypes";
import { EMOJI_UNICODE_POSITIVE } from "../Constants/EmojiConstants";
import { setLanguageFromLocale } from "../../config/Localizations";
import { tsthreeConfig } from "../../config/tsthreeConfig";
import { FirebaseSingleton } from "../../engine/FirebaseSingleton";
import * as Mousetrap from "mousetrap";
import { FORMATS, Renderer, Spritesheet } from "pixi.js";
import { constructSceneGraphString } from "../../engine/HelperFunctions/constructSceneGraphString";
import {HospitalMain} from "./HospitalMain";
import {MailCore} from "../Logic/MailCore";
import { HospitalNetCore } from "../Logic/HospitalNetCore";
import { loadFont } from "../../engine/HelperFunctions/loadFont";
import { GAME_FONT, GAME_FONT_BOLD, GAME_FONT_ITALIC } from "../Constants/Constants";
import { HospitalIDMapper } from "../Data/HospitalIDMapper";
import { CRTEffectHandler } from "../UI/CRTEffectSingleton";
import { ShowBluescreen } from "../Logic/ShowBluescreen";
import { LoginScreen } from "./LoginScreen";
import { IHospitalCore } from "../Types/IHospitalCore";

export class GameInit extends State {

    // private _savedData: Record<string, string>;
    private _entryPoint: string;
    private _entryPointData: { [key: string]: unknown } | null;

    private static configureCheats(_engine: Engine): void {
        const saveData = (_engine: Engine): Promise<void> => {
            const data = PlayerDataSingleton.export();
            return _engine.getSaveHandler().save(data).catch((err) => {
                console.error(err);
                return _engine.getSaveHandler().save(data).catch((err) => {
                    console.error(err);
                });
            });
        };

        const key: string = (GAME_DEBUG_MODE || PUPPETEER_MODE) ? "CHEATS" : EMOJI_UNICODE_POSITIVE.NERDY;
        ((window as unknown as { [key: string]: unknown })[key]) = {
            sendEmail: (id: string) => {
                MailCore.sendEmailToPlayer(id);
            },
            getHospitalNetCore: HospitalNetCore,
            getHospitalCore: ENGINE.getWASM("HospitalCore"),
            addXP: async (_amt: number): Promise<void> => {
                PlayerDataSingleton.addDocScore(_amt);
            },
            parseIdToString: (_id: string): string => {
                return HospitalMain.idToStringObject[_id];
            },
            resetData: (): void => {
                _engine.getSaveHandler().clear()
                    .then(() => {
                        console.log("Cleared data successfully; refresh page");
                    })
                    .catch((err) => {
                        console.error("Failed to clear data %o", err);
                    });
            },
            exportData: (): void => {
                console.log(PlayerDataSingleton.export(true));
            },
        };
        if (ENGINE_DEBUG_MODE) {
            // this is for cheats that have no place at all in Production
            const windowCheatObj = ((window as unknown as { [key: string]: any })[key]);
            windowCheatObj["setFirebaseProgress"] = async (_progress: number) => {
                await FirebaseSingleton.setProgressOnFirebase(
                    _engine.platformSDK.getPlayerId(),
                    await FirebaseSingleton.getLoggedInUserToken(),
                    _progress
                );
                console.log("Done");
            };
            windowCheatObj["getFriendProgress"] = async () => {
                const signedInfo =
                    await _engine.platformSDK.getSignedInfo(
                        JSON.stringify({
                                friends: (await _engine.platformSDK.getFriends())
                                    .map((e) => {
                                        return e.uid;
                                    })
                            }
                        )
                    );
                console.log(await FirebaseSingleton.getFriendsProgress(
                    signedInfo.getPlayerID(),
                    signedInfo.getSignature()
                ));
                console.log("Done");
            };
        }
    }

    async preload(_engine: Engine): Promise<void> {
        // return new Promise<void>(async (resolve, reject) => {
        ENGINE.recordLoadtime("GameInit.preload");
        GameInit.configureCheats(_engine);

        const keys =
            HelperFunctions.enumKeys(SAVE_KEYS).map((e) => SAVE_KEYS[e])
                .filter((e) => {
                    return PER_SLOT_KEYS.indexOf(e) === -1;
                });
        const _savedData = await _engine.getSaveHandler().load(keys) as unknown as Record<string, string>;

        PlayerDataSingleton.initialize(_savedData);

        this._entryPoint = await _engine.platformSDK.getEntryPointAsync();
        this._entryPointData = _engine.platformSDK.getEntryPointData();

        // const tournaments = await _engine.platformSDK.getTournamentsAsync()
        //     .catch((err) => {
        //         console.log(err);
        //         return err;
        //     });
        //
        // // Tournaments hack because they're handled differently smh
        // const tournament = await _engine.platformSDK.getTournamentAsync().catch((e) => {
        //     if (e && e.code && e.code === "TOURNAMENT_NOT_FOUND") {
        //         console.log("No tournament found.");
        //     } else {
        //         console.warn(e);
        //     }
        //     return null;
        // });
        //
        // if (tournaments && tournaments.length > 0) {
        //     PlayerDataSingleton.syncTournaments(tournaments);
        // }
        // if (tournament && Math.round(tournament.getEndTime() * 1000) > Date.now()) {
        //     this._entryPoint = EntrypointTypes.TOURNAMENT;
        //     this._entryPointData = tournament.getPayload();
        //     if (typeof this._entryPointData === "string") {
        //         try {
        //             this._entryPointData = JSON.parse(this._entryPointData);
        //             this._entryPointData.id = tournament.getID();
        //             this._entryPointData.cid = tournament.getContextID();
        //             this._entryPointData.score = PlayerDataSingleton.getTournamentScore(
        //                 this._entryPointData.id as string
        //             );
        //         } catch (err) {
        //             console.error("Failed to parse payload for tournament");
        //             console.error(err);
        //             this._entryPointData = null;
        //             this._entryPoint = EntrypointTypes.NONE;
        //         }
        //     }
        // }

        setLanguageFromLocale(
            _engine.platformSDK.getPlayerLocale()
            // "en_GB"
        );

        _engine.platformSDK.setLoadingProgress(70);

        if(_engine.hasPIXIResource("Windows95Spritesheet")) {
            const success = _engine.processSpritesheet(
                _engine.getPIXIResource("Windows95Spritesheet") as Spritesheet
            );
            if(success) {
                console.log("Successfully parsed Windows95Spritesheet");
            }
        }

        const promises = [];
        promises.push(HospitalIDMapper.initialize());
        promises.push(
            loadFont(GAME_FONT)
        );
        promises.push(
            loadFont(GAME_FONT_BOLD)
        );
        promises.push(
            loadFont(GAME_FONT_ITALIC)
        );
        await Promise.allSettled(promises).catch((err) => ShowBluescreen(err.stack)).then(() => {});
        return;
    }

    async onAwake(_engine: Engine, _params?: unknown): Promise<void> {
        if(!HelperFunctions.isWebAssemblySupported()) {
            ShowBluescreen(
                "\n\nThis game requires your browser to\nsupport WebAssembly.\n\nPlease enable WebAssembly and refresh\nthe page.",
                true,
            );
            ENGINE.getTicker().start();
            return;
        }

        window.onerror = (a, b, c, d, e) => {
            console.error(e);
            const formattedError = `${e.stack}`.replace(/Error: /g, " ");
            ShowBluescreen(formattedError);
        };
        window.onunhandledrejection = (a) => {
            console.error(a);
            ShowBluescreen(a.reason?.stack || "Unknown asynchronous error; press enter.");
        };

        const hospitalCore = ENGINE.getWASM<IHospitalCore>("HospitalCore");
        if(hospitalCore && hospitalCore.isInitialized() == false) {
            console.log("Initializing HospitalCore WASM...");
            hospitalCore.initialize();
            if(hospitalCore.isInitialized() === false) {
                throw new Error("Failed to initialize HospitalCore WASM!");
            } else {
                console.log("Successfully initialized HospitalCore WASM.");
            }
        } else if(!hospitalCore) {
            console.error(hospitalCore);
            throw new Error("Failed to initialize HospitalCore WASM!");
        } else {
            console.log("HospitalCore WASM already initialized, skipping...");
        }

        // console.log(hospitalCore.generateCase({
        //     filters: {
        //         departmentFilter: [
        //             0, 1, 2, 3, 4, 5
        //         ]
        //     }
        // }));

        const entryPointType: EntrypointTypes = this.checkEntryPoint(this._entryPointData);
        if (entryPointType) {
            switch (entryPointType) {
                case EntrypointTypes.NONE:
                default:
                    break;
            }
        }

        if(tsthreeConfig.pauseOnFocusLoss) {
            let focusLost = false;
            ENGINE.platformSDK.addOnPauseCallback(() => {
                ENGINE.getTicker().stop();
                focusLost = true;
            });
            ENGINE.platformSDK.addOnResumeCallback(() => {
                if(focusLost) return;
                ENGINE.getTicker().start();
            });
        }

        // Detect mouseover and signal to parent to disable scroll
        window.addEventListener('mouseover', function() {
            try {
                parent.document.body.style.overflow = 'hidden';
            } catch(err) {
                console.warn(err);
            }
        });

        // Detect mouseout and signal to parent to enable scroll
        window.addEventListener('mouseout', function() {
            try {
                parent.document.body.style.overflow = '';
            } catch(err) {
                console.warn(err);
            }
        });

       // _engine.setMaxFPS(60);

        console.log(`Entrypoint type was: ${entryPointType}`);
        console.log(this._entryPointData);

        this.tryAddMousetrapBindings();

        if(PlayerDataSingleton.getCRTEffectEnabled()) {
            if(GAME_DEBUG_MODE) {
                console.log("adding CRT effect");
            }
            CRTEffectHandler.addToGame();
        }

        // ~this is a hack, ooo baby it's a hack~
        try {
            if(
                __VERSION[2] == "4" &&
                window.localStorage.getItem(SAVE_KEYS.Version)
            ) {
                window.localStorage.clear();
                console.log("Cleared localstorage for v0.4.0+");
            }
        } catch(err) {
            console.error(err);
        }

        // if(tsthreeConfig.autoLoadState) {
            ENGINE.changeState(
                // tsthreeConfig.autoLoadState
                new LoginScreen()
                // new CharacterCreatorTestSuite(),
                // new GolfTest(),
                // new Windows95()
                // new StressTestState()
                // new HospitalMain()
            );
        // }
    }

    private tryAddMousetrapBindings(): void {
        if(ENGINE_DEBUG_MODE || GAME_DEBUG_MODE) {
            let drawCount = 0;
            let printDrawCount = false;
            const renderer = ENGINE.getUIManager().getRenderer();
            // @ts-ignore
            const drawElements = renderer.gl.drawElements;
            // @ts-ignore
            renderer.gl.drawElements = (...args: any[]) => {
                // @ts-ignore
                drawElements.call(renderer.gl, ...args);
                drawCount++;
            }; // rewrite drawElements to count draws
            ENGINE.getTicker().add(() => {
                if(printDrawCount) {
                    console.log("Draw count %i", drawCount);
                    printDrawCount = false;
                }
                drawCount = 0; // clear count per frame
            });

            const log = () => {
                printDrawCount = true;
                const renderer = ENGINE.getUIManager().getRenderer() as Renderer;
                console.log("%ix%i", renderer.width, renderer.height);
                // @ts-ignore
                const buffers = renderer.geometry.managedGeometries;
                const rts = renderer.framebuffer.managedFramebuffers;
                const formatToSize: { [key: number]: number } = {
                    [FORMATS.RGB]: 3,
                    [FORMATS.RGBA]: 4,
                    [FORMATS.DEPTH_COMPONENT]: 3,
                    [FORMATS.DEPTH_STENCIL]: 4,
                    [FORMATS.ALPHA]: 1,
                    [FORMATS.LUMINANCE]: 1,
                    [FORMATS.LUMINANCE_ALPHA]: 2
                };
                const textures = renderer.texture.managedTextures;
                let textureTotalMem = 0;
                for (const key in textures) {
                    const t = textures[key];
                    textureTotalMem += t.width * t.height * formatToSize[t.format];
                }
                let bufferTotalMem = 0;
                for (const key in buffers) {
                    const b = buffers[key];
                    b.buffers.forEach((e) => {
                        bufferTotalMem += e.data.byteLength;
                    });
                }
                console.log(`{
                count: {
                    textures: ${textures.length},
                    buffers: ${Object.keys(buffers).length},
                    renderTextures: ${rts.length}
                },
                mem: {
                    // in MBs
                    textures: ${textureTotalMem / (1024 * 1024)},
                    buffers: ${bufferTotalMem / (1024 * 1024)}
                }
            }`);

                console.log(renderer);
            };
            Mousetrap.bind("ctrl+alt+r", log);
            Mousetrap.bind("b l u e s c r e e n", () => {
                ShowBluescreen("Debug Error, no worries :)");
            });
            Mousetrap.bind("c r a s h", () => {
                throw new Error("Test");
            });
            Mousetrap.bind("s c e n e", () => {
                console.log(constructSceneGraphString(ENGINE.getActiveState()["scene"]));
            });
        }
    }

    private checkEntryPoint(data: { [key: string]: unknown }): EntrypointTypes {
        return (data && data["from"] as EntrypointTypes) || EntrypointTypes.NONE;
    }

    onDestroy(engine: Engine): void {
    }

    onStep(_engine: Engine): void {
    }
}
